import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import LandingNavbar from '../components/LandingNavbar';

const LandingPage = () => {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Check if user is authenticated on component mount
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    setIsAuthenticated(!!token);
  }, []);

  const handleLearnMore = () => {
    // Smooth scroll to features section
    document.getElementById('features').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-100">
      <LandingNavbar />
      
      {/* Hero Section */}
      <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            {t('landing.hero.title', 'Effortless School Menu Management')}
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t('landing.hero.subtitle', 'Organize, translate, and publish your school lunch menus across multiple languages with our intuitive platform.')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to={isAuthenticated ? "/dashboard" : "/signup"}
              className="px-8 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {isAuthenticated 
                ? t('landing.hero.goToDashboard', 'Go to Dashboard') 
                : t('landing.hero.getStarted', 'Sign Up / Log In')}
            </Link>
            <button
              onClick={handleLearnMore}
              className="px-8 py-3 text-lg font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              {t('landing.hero.learnMore', 'Explore Features')}
            </button>
          </div>
        </div>
        
        <div className="mt-16 bg-white p-6 rounded-lg shadow-lg max-w-5xl mx-auto">
          {!imageError ? (
            <img 
              src="/images/menu-editor-demo.jpeg"
              alt="School Menu App Editor Preview"
              className="w-full rounded-md border"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="bg-gray-200 rounded-md w-full aspect-[2/1] flex items-center justify-center">
              <p className="text-gray-500 text-lg">{t('landing.hero.imagePlaceholder', 'App Editor Preview')}</p>
            </div>
          )}
        </div>
      </section>
      
      {/* Features Section */}
      <section id="features" className="py-16 px-4 md:px-8 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-12">
          {t('landing.features.title', 'Core Features')}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-items-center">
          {/* Feature 1: Menu Editor */}
          <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {t('landing.features.menuEditor.title', 'Intuitive Menu Editor')}
            </h3>
            <p className="text-gray-600">
              {t('landing.features.menuEditor.description', 'Easily create and update monthly menus. Add dishes, courses, notes, and manage nutrient data.')}
            </p>
          </div>
          
          {/* Feature 2: Multilingual */}
          <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {t('landing.features.translation.title', 'Seamless Translations')}
            </h3>
            <p className="text-gray-600">
              {t('landing.features.translation.description', 'Translate your menu items into multiple languages (EN, CA, ES supported out-of-the-box) to cater to your community.')}
            </p>
          </div>
          
          {/* Feature 3: Collaboration */}
          <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {t('landing.features.collaboration.title', 'Team Collaboration')}
            </h3>
            <p className="text-gray-600">
              {t('landing.features.collaboration.description', 'Manage user roles (Admin, Editor) to control who can edit menus and manage school settings.')}
            </p>
          </div>

          {/* Feature 4: Dietary Adaptations - Removed md:col-start-2 */}
          <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {t('landing.features.substitution.title', 'Dietary Adaptations')}
            </h3>
            <p className="text-gray-600">
              {t('landing.features.substitution.description', 'Receive suggestions for adapting menus to special dietary needs based on previously used dishes.')}
            </p>
          </div>

          {/* Feature 5: Nutrient Tracking */}
          <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-12v4m-2-2h4m5 4v4m-2-2h4M17 3l-1.17.585A15.002 15.002 0 0112 15c-.942 0-1.868-.103-2.747-.292L8 15M7 11v4m-2-2h4" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {t('landing.features.nutrients.title', 'Nutrient Information')}
            </h3>
            <p className="text-gray-600">
              {t('landing.features.nutrients.description', 'Associate dishes with detailed nutrient data for better meal planning and information sharing.')}
            </p>
          </div>

          {/* Feature 6: Community Access (NEW) */}
          <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {t('landing.features.communityAccess.title', 'Easy Community Access')}
            </h3>
            <p className="text-gray-600">
              {t('landing.features.communityAccess.description', 'Provide parents and students with simple access to view the latest menus from any device through a dedicated public page.')}
            </p>
          </div>

        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-16 px-4 md:px-8 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">
            {t('landing.cta.title', 'Ready to Streamline Your Menus?')}
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            {t('landing.cta.subtitle', 'Create an account or log in to start managing your school menus today.')}
          </p>
          
          <div className="flex justify-center">
            <Link 
              to={isAuthenticated ? "/dashboard" : "/signup"}
              className="px-8 py-3 text-lg font-medium text-blue-600 bg-white rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isAuthenticated 
                ? t('landing.hero.goToDashboard', 'Go to Dashboard') 
                : t('landing.hero.getStarted', 'Sign Up / Log In')}
            </Link>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 px-4 md:px-8 bg-gray-800 text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('landing.footer.about', 'About School Menus')}</h3>
            <p className="text-gray-400">
              {t('landing.footer.aboutText', 'A platform designed to simplify the creation, translation, and publication of school lunch menus.')}
            </p>
          </div>
          
          {/* Placeholder Column 1 (Can be removed or repurposed) */}
           <div>
             <h3 className="text-lg font-semibold mb-4 text-gray-800 pointer-events-none select-none">.</h3> {/* Invisible header for alignment */}
             <ul className="space-y-2">
               <li>&nbsp;</li> {/* Placeholder for alignment */}
             </ul>
           </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('landing.footer.legal', 'Legal')}</h3>
            <ul className="space-y-2">
              {/* Removed Privacy Policy link for now, can be added back if page exists */}
              {/* <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">{t('landing.footer.privacy', 'Privacy Policy')}</Link></li> */}
              <li><Link to="/terms" className="text-gray-400 hover:text-white transition-colors">{t('landing.footer.terms', 'Terms & Conditions')}</Link></li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('landing.footer.contact', 'Contact')}</h3>
            <p className="text-gray-400">{t('landing.footer.email', 'info@schoolmenus.xyz')}</p>
            {/* Add other contact info if available */}
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-gray-700 text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} School Menus. {t('landing.footer.rights', 'All rights reserved.')}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 